/**
 * Artifact Detection & Management
 * Scans AI responses for extractable artifacts (code, tables, diagrams, documents)
 */

import { store, addArtifact } from './appState.js';
import { db } from './db.js';

/**
 * Scan a completed message for artifacts and create them
 * @param {string} conversationId
 * @param {string} messageId
 * @param {string} content - Raw message content
 * @returns {Object[]} Created artifacts
 */
export function detectAndCreateArtifacts(conversationId, messageId, content) {
  if (!content) return [];

  const artifacts = [];

  // Detect code blocks (10+ lines)
  const codeBlocks = detectCodeBlocks(content);
  for (const block of codeBlocks) {
    artifacts.push(createArtifactObj({
      conversationId,
      messageId,
      type: 'code',
      title: block.title,
      content: block.content,
      language: block.language,
    }));
  }

  // Detect mermaid diagrams
  const mermaidBlocks = detectMermaidBlocks(content);
  for (const block of mermaidBlocks) {
    artifacts.push(createArtifactObj({
      conversationId,
      messageId,
      type: 'diagram',
      title: block.title,
      content: block.content,
      language: 'mermaid',
    }));
  }

  // Detect markdown tables (3+ rows)
  const tables = detectTables(content);
  for (const table of tables) {
    artifacts.push(createArtifactObj({
      conversationId,
      messageId,
      type: 'table',
      title: table.title,
      content: table.content,
      language: '',
    }));
  }

  // Detect long document content (500+ chars of prose, only if no other artifacts found)
  if (artifacts.length === 0) {
    const doc = detectDocument(content);
    if (doc) {
      artifacts.push(createArtifactObj({
        conversationId,
        messageId,
        type: 'document',
        title: doc.title,
        content: doc.content,
        language: '',
      }));
    }
  }

  // Store artifacts in state and DB
  for (const artifact of artifacts) {
    addArtifact(artifact);
    db.saveArtifact(artifact);
  }

  return artifacts;
}

function createArtifactObj({ conversationId, messageId, type, title, content, language }) {
  return {
    id: crypto.randomUUID(),
    conversationId,
    messageId,
    type,
    title,
    content,
    language: language || '',
    versions: [{ content, timestamp: Date.now() }],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Detect code blocks with 10+ lines
 */
function detectCodeBlocks(content) {
  const results = [];
  const regex = /```(\w*)\n([\s\S]*?)```/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const lang = match[1];
    const code = match[2].trim();
    const lines = code.split('\n').length;

    // Skip mermaid (handled separately) and short blocks
    if (lang.toLowerCase() === 'mermaid') continue;
    if (lines < 10) continue;

    const title = inferCodeTitle(code, lang);
    results.push({ language: lang, content: code, title });
  }

  return results;
}

/**
 * Detect mermaid diagram blocks
 */
function detectMermaidBlocks(content) {
  const results = [];
  const regex = /```mermaid\n([\s\S]*?)```/gi;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const code = match[1].trim();
    results.push({ content: code, title: 'Diagram' });
  }

  return results;
}

/**
 * Detect markdown tables with 3+ data rows
 */
function detectTables(content) {
  const results = [];
  const regex = /((?:^\|.+\|[ \t]*\n)+)/gm;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const tableBlock = match[1].trim();
    const rows = tableBlock.split('\n');

    if (rows.length < 2) continue;

    // Check for separator row
    const isSeparator = /^\|[\s\-:]+([\s\-:]+\|)+$/.test(rows[1]);
    if (!isSeparator) continue;

    const dataRows = rows.length - 2; // minus header and separator
    if (dataRows < 3) continue;

    // Extract title from header
    const headers = rows[0].split('|').filter(Boolean).map(h => h.trim());
    const title = headers.length <= 3 ? headers.join(' / ') : `Table (${headers.length} columns)`;

    results.push({ content: tableBlock, title });
  }

  return results;
}

/**
 * Detect long document/prose content (500+ chars)
 */
function detectDocument(content) {
  // Strip code blocks and tables to get just prose
  let prose = content
    .replace(/```[\s\S]*?```/g, '')
    .replace(/((?:^\|.+\|[ \t]*\n)+)/gm, '')
    .trim();

  if (prose.length < 500) return null;

  // Extract title from first heading or first line
  const headingMatch = prose.match(/^#+ (.+)$/m);
  const title = headingMatch ? headingMatch[1].slice(0, 50) : prose.slice(0, 40).replace(/\n/g, ' ') + '...';

  return { content: prose, title: title.trim() };
}

/**
 * Infer a title for a code block
 */
function inferCodeTitle(code, lang) {
  // Look for class/function/module name
  const classMatch = code.match(/(?:class|interface|struct|enum)\s+(\w+)/);
  if (classMatch) return classMatch[1] + '.' + extForLang(lang);

  const funcMatch = code.match(/(?:function|def|fn|func)\s+(\w+)/);
  if (funcMatch) return funcMatch[1] + '.' + extForLang(lang);

  const exportMatch = code.match(/export\s+(?:default\s+)?(?:function|class|const)\s+(\w+)/);
  if (exportMatch) return exportMatch[1] + '.' + extForLang(lang);

  return (lang || 'code') + ' snippet.' + extForLang(lang);
}

function extForLang(lang) {
  const map = {
    javascript: 'js', typescript: 'ts', python: 'py', rust: 'rs',
    go: 'go', java: 'java', html: 'html', css: 'css',
    json: 'json', yaml: 'yml', sql: 'sql', bash: 'sh',
    shell: 'sh', jsx: 'jsx', tsx: 'tsx', c: 'c', cpp: 'cpp',
  };
  return map[lang?.toLowerCase()] || 'txt';
}

/**
 * Get artifacts for a specific message
 */
export function getArtifactsForMessage(messageId) {
  const state = store.getState();
  return Object.values(state.artifacts.items).filter(a => a.messageId === messageId);
}

/**
 * Get artifacts for a conversation
 */
export function getArtifactsForConversation(conversationId) {
  const state = store.getState();
  return Object.values(state.artifacts.items).filter(a => a.conversationId === conversationId);
}

/**
 * Update artifact content and create a new version
 */
export function updateArtifactContent(artifactId, newContent) {
  const state = store.getState();
  const artifact = state.artifacts.items[artifactId];
  if (!artifact) return;

  const MAX_VERSIONS = 20;
  const versions = [...artifact.versions, { content: newContent, timestamp: Date.now() }].slice(-MAX_VERSIONS);

  store.setState({
    artifacts: {
      items: {
        ...state.artifacts.items,
        [artifactId]: { ...artifact, content: newContent, versions, updatedAt: Date.now() },
      },
    },
  });

  db.saveArtifact({ ...artifact, content: newContent, versions, updatedAt: Date.now() });
}

/**
 * Restore artifact to a previous version
 */
export function restoreArtifactVersion(artifactId, versionIndex) {
  const state = store.getState();
  const artifact = state.artifacts.items[artifactId];
  if (!artifact || !artifact.versions[versionIndex]) return;

  const restoredContent = artifact.versions[versionIndex].content;
  updateArtifactContent(artifactId, restoredContent);
}

/**
 * Load artifacts from DB into state on startup
 */
export async function loadStoredArtifacts() {
  try {
    const artifacts = await db.loadAllArtifacts();
    if (artifacts.length > 0) {
      const items = {};
      for (const art of artifacts) {
        items[art.id] = art;
      }
      store.setState({ artifacts: { items } });
    }
  } catch (error) {
    console.error('Failed to load artifacts:', error);
  }
}
